/*----------------------------------
- NEWSLETTER SUBSCRIPTION PAGE
- Ported from the 2026-09 Newsletter mockups: hero with the first capture and the
- schedule card, the three sends with an illustrative preview each, the audiences,
- the trust row and the closing ink band. The top bar, the phone action bar and
- the footer stay the shared site Header/Footer.
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect } from 'preact/hooks';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import {
    NEWSLETTER_PAGE_CONTENT,
} from '@/client/catalogs/landing/newsletter/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import NewsletterSubscribeForm from '@/client/pages/landing/components/newsletter/NewsletterSubscribeForm';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildNewsletterGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import {
    HERO_SECTION_CLASS,
    PAGE_CONTAINER_CLASS,
    SECTION_SPACING_CLASS,
    WHITE_CARD_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { getHumanPageRelationships } from '@/common/catalogs/landing/internalLinks';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { DOMAINING_NEWS_INDEX_PATH } from '@uniquedomains/components/contracts/news/public';
import { NEWSLETTER_PAGE_PATH } from '@uniquedomains/components/contracts/newsletter/public';

import '../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type NewsletterPageProps = {
    navigation?: LandingHeaderDomainsNavigation;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'newsletter';

/** The phone action bar's primary action scrolls here instead of opening a route. */
const SUBSCRIBE_ANCHOR_ID = 'subscribe';

const SECTION_HEADING_CLASS =
    'text-pretty text-[28px] font-bold leading-[1.1] tracking-[-0.04em] text-[#1b1d1e]';
const SEND_TAG_CLASS =
    'inline-flex items-center rounded-full bg-[#f2e97e] px-[11px] py-[5px] text-[10.5px] font-extrabold uppercase tracking-[0.06em] text-[#1b1d1e] lg:px-[13px] lg:py-1.5 lg:text-[11px]';
const CADENCE_CLASS = 'text-[11.5px] font-bold tabular-nums text-[#6a695e]';

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * Both captures record the accepted subscription here: the shared form never tracks, so
 * the payload names the page's source and never the address.
 */
const recordSubscription = () => track('newsletter_subscribe_submitted', { source: 'website_newsletter_page' });

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function NewsletterPage({ navigation = EMPTY_LANDING_NAVIGATION }: NewsletterPageProps) {
    /*----------------------------------
    - INIT
    ----------------------------------*/

    const content = NEWSLETTER_PAGE_CONTENT;
    const geoContext = buildNewsletterGeoContext();
    const relatedLinks = getHumanPageRelationships(LANDING_WEBSITE_PAGE_ROUTES.newsletter).map(
        ({ label, targetPath }) => ({ label, href: targetPath }),
    );

    /*----------------------------------
    - EFFECTS
    ----------------------------------*/

    useEffect(() => {
        if (typeof window === 'undefined') return;

        track('page_view', {
            page: TRACKING_PAGE,
            path: window.location.pathname + window.location.search,
        });
    }, []);

    /*----------------------------------
    - RENDER
    ----------------------------------*/

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <Header
                links={LANDING_WEBSITE_LINKS}
                navigation={navigation}
                currentPath={NEWSLETTER_PAGE_PATH}
                trackingPage={TRACKING_PAGE}
                mobileActions={{
                    secondary: {
                        label: content.actionBar.secondaryLabel,
                        href: DOMAINING_NEWS_INDEX_PATH,
                        testId: 'newsletter-mobile-latest-edition',
                    },
                    primary: {
                        label: content.actionBar.primaryLabel,
                        href: `#${SUBSCRIBE_ANCHOR_ID}`,
                        testId: 'newsletter-mobile-subscribe',
                    },
                }}
            />

            <main id="top">
                {/*
                    HERO. The schedule card is 50px narrower than the mockup's 430px and the
                    gap 14px tighter, so the title column keeps the mockup's own 740px and the
                    headline still breaks over two lines inside the shared 1176px measure.
                */}
                <section
                    class={`${PAGE_CONTAINER_CLASS} ${HERO_SECTION_CLASS} grid gap-[22px] lg:grid-cols-[minmax(0,1fr)_380px] lg:items-end lg:gap-14`}
                >
                    <div>
                        <h1 class="text-pretty text-[40px] font-bold leading-none tracking-[-0.05em] text-[#1b1d1e] lg:text-[72px] lg:leading-[0.97]">
                            {content.hero.headline}
                        </h1>
                        <p class="mt-4 max-w-[600px] text-pretty text-base leading-[1.6] text-[#66696b] lg:mt-[22px] lg:text-[19px] lg:leading-[1.65]">
                            {content.hero.lede}
                        </p>

                        {/* The anchor of the phone action bar, offset so the top bar cannot cover the field. */}
                        <div
                            id={SUBSCRIBE_ANCHOR_ID}
                            class="mt-[22px] max-w-[620px] scroll-mt-24 lg:mt-8 lg:scroll-mt-0"
                        >
                            <NewsletterSubscribeForm
                                source="website_newsletter_page"
                                instanceId="newsletter-hero"
                                frame="pill"
                                submitLabel={content.hero.submitLabel}
                                onSubscribed={recordSubscription}
                            />
                        </div>

                        <p class="mt-3 px-1 text-[13px] leading-[1.5] text-[#66696b] lg:mt-3.5 lg:text-[13.5px]">
                            {content.hero.reassurance}
                        </p>
                    </div>

                    <div class="flex flex-col gap-2.5 lg:gap-3.5 lg:pb-1.5">
                        {/* Desktop only: on a phone the caption below the card already names the three sends. */}
                        <p class="hidden text-sm font-semibold text-[#4a4d4e] lg:block">
                            {content.schedule.label}
                        </p>
                        <ul class="flex flex-col gap-px overflow-hidden rounded-[18px] border border-[rgba(27,29,30,0.08)] bg-[rgba(27,29,30,0.08)] lg:rounded-[20px]">
                            {content.schedule.items.map((item) => (
                                <li
                                    key={item.id}
                                    class="flex items-center justify-between gap-3 bg-white px-4 py-3.5 lg:gap-4 lg:px-5 lg:py-4"
                                >
                                    <span class="text-sm font-semibold tracking-[-0.015em] text-[#1b1d1e]">
                                        {item.tag}
                                    </span>
                                    <span class={`whitespace-nowrap ${CADENCE_CLASS}`}>
                                        {item.cadence}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p class="px-1 text-[12.5px] leading-[1.6] text-[#66696b] lg:px-0">
                            {content.schedule.caption}
                        </p>
                    </div>
                </section>

                {/* WHAT IT SAVES YOU */}
                <section class={`${PAGE_CONTAINER_CLASS} ${SECTION_SPACING_CLASS} lg:pt-[88px]`}>
                    <div class="flex flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between lg:gap-6">
                        <h2 class={`${SECTION_HEADING_CLASS} lg:text-4xl`}>{content.savings.heading}</h2>
                        <p class="max-w-[420px] text-pretty text-[14.5px] leading-[1.6] text-[#66696b]">
                            {content.savings.support}
                        </p>
                    </div>

                    {/* Two columns; an odd last card (the Thursday one, since the agenda made three) spans both rather than sitting alone at half width. */}
                    <div class="mt-[18px] grid gap-2.5 lg:mt-[26px] lg:grid-cols-2 lg:gap-4">
                        {content.savings.cards.map((card) => (
                            <article
                                key={card.id}
                                class={`${WHITE_CARD_CLASS} px-5 py-[22px] lg:grid lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-6 lg:rounded-[24px] lg:px-8 lg:pb-8 lg:pt-[30px] lg:last:odd:col-span-2`}
                            >
                                <div>
                                    <p class="flex items-center gap-2.5">
                                        <span class={SEND_TAG_CLASS}>{card.tag}</span>
                                        <span class={CADENCE_CLASS}>{card.cadence}</span>
                                    </p>
                                    <h3 class="mt-3 text-pretty text-[21px] font-bold leading-[1.15] tracking-[-0.03em] text-[#1b1d1e] lg:text-2xl lg:tracking-[-0.035em]">
                                        {card.headline}
                                    </h3>
                                    <p class="mt-2.5 text-[14.5px] leading-[1.6] text-[#66696b] lg:mt-3 lg:text-[15px] lg:leading-[1.65]">
                                        {card.body}
                                    </p>
                                </div>

                                {/* Illustrative sample of the send, never a live row: hidden from assistive tech. */}
                                <div
                                    aria-hidden="true"
                                    class="mt-3.5 flex flex-col gap-1.5 rounded-[14px] border border-[#ece8df] bg-[#f8f6f1] p-3 lg:mt-0 lg:gap-2 lg:rounded-2xl lg:p-3.5"
                                >
                                    <span class="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#6a695e]">
                                        {card.previewLabel}
                                    </span>
                                    {card.preview.map((row) => (
                                        <span
                                            key={row.text}
                                            class="flex justify-between gap-2 text-[12.5px] text-[#4a4d4e] lg:text-xs"
                                        >
                                            <span class="truncate">{row.text}</span>
                                            <span class="flex-none font-bold text-[#66696b]">
                                                {row.meta}
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* AUDIENCES */}
                <section
                    class={`${PAGE_CONTAINER_CLASS} ${SECTION_SPACING_CLASS} grid gap-[18px] lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-[60px] lg:pt-[72px]`}
                >
                    <h2 class={`${SECTION_HEADING_CLASS} lg:text-[34px]`}>{content.audiences.heading}</h2>
                    <ul class="border-t border-[rgba(27,29,30,0.12)] lg:grid lg:grid-cols-3">
                        {content.audiences.items.map((audience) => (
                            <li
                                key={audience.id}
                                class="flex flex-col gap-1.5 border-b border-[rgba(27,29,30,0.1)] py-[18px] lg:gap-2.5 lg:border-b-0 lg:pb-[26px] lg:pr-7 lg:pt-6"
                            >
                                <span class="text-[17px] font-bold tracking-[-0.02em] text-[#1b1d1e] lg:text-lg lg:tracking-[-0.025em]">
                                    {audience.who}
                                </span>
                                <span class="text-pretty text-sm leading-[1.6] text-[#66696b] lg:text-[14.5px]">
                                    {audience.body}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* CLOSING BAND */}
                <section class={`${PAGE_CONTAINER_CLASS} pt-14`}>
                    <div class="rounded-[24px] bg-[#1b1d1e] px-5 py-6 lg:grid lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center lg:gap-11 lg:rounded-[26px] lg:px-11 lg:py-10">
                        <div>
                            <h2 class="text-pretty text-[22px] font-bold tracking-[-0.035em] text-white lg:text-[30px] lg:tracking-[-0.04em]">
                                {content.ctaBand.heading}
                            </h2>
                            <p class="mt-2 max-w-[520px] text-[13.5px] leading-[1.55] text-white/60 lg:mt-2.5 lg:text-[14.5px]">
                                {content.ctaBand.body}
                            </p>
                            <a
                                href={DOMAINING_NEWS_INDEX_PATH}
                                class="mt-3 inline-flex w-fit items-center gap-2 text-[13px] font-bold text-white/50 transition hover:text-[#ffffffcc]"
                            >
                                {content.archiveLink.label}
                                <span aria-hidden="true">&rarr;</span>
                            </a>
                        </div>

                        {/*
                            Desktop only: on a phone the fixed action bar carries Subscribe, and a
                            second field here would repeat the hero capture one screen above the footer.
                        */}
                        <NewsletterSubscribeForm
                            source="website_newsletter_page"
                            instanceId="newsletter-cta-band"
                            tone="dark"
                            size="lg"
                            submitLabel={content.ctaBand.submitLabel}
                            className="hidden w-full shrink-0 lg:flex lg:w-[420px]"
                            onSubscribed={recordSubscription}
                        />
                    </div>
                </section>

                <RelatedLinks links={relatedLinks} />

                <PageGeoBlock className="pb-4 pt-10" geo={geoContext} surface="newsletter" />
            </main>

            <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={TRACKING_PAGE} />
        </div>
    );
}
